import { Dayjs } from 'dayjs'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Shift } from '../../types/schedule.model'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { dayjs, toDateKey } from '../../utils/schedule'
import styles from './MonthCalendar.module.scss'
import { isAllDayType, shiftTypeDefinition } from '../../types/shiftType.model'

interface MonthCalendarProps {
  /** Any day inside the month to render */
  month: Dayjs
  /** Every shift the store holds; filter to the day yourself */
  shiftsForDay: (day: Dayjs) => Shift[]
  employeeColor: (userId: string) => string
  employeeName: (userId: string) => string
  /** Clicking a day cell opens that day in the day view */
  onSelectDay: (day: Dayjs) => void
  isLoading?: boolean
}

const DAYS_IN_WEEK = 7

/**
 * How many dots a cell draws before the rest collapse into a "+N".
 *
 * A cell is a seventh of the card, so the narrowest one this layout is asked to
 * survive is around 5rem of content box; a dot plus its gap costs 0.75rem and
 * the "+N" needs roughly 1.5rem. Four is what still fits there on one line, a
 * fixed number rather than a measured one, because measuring every cell would
 * mean a ResizeObserver per day to decide something the column width already
 * fixes for the whole month. Anything the cap misjudges is clipped by
 * `.dots`, never wrapped.
 */
const MAX_VISIBLE_DOTS = 4

const isToday = (day: Dayjs) => day.isSame(dayjs(), 'day')

function MonthCalendar(props: MonthCalendarProps) {
  const {
    month,
    shiftsForDay,
    employeeColor,
    employeeName,
    onSelectDay,
    isLoading
  } = props
  const { t, i18n } = useTranslation()

  const gridRef = useRef<HTMLDivElement | null>(null)

  /**
   * Whole weeks, Monday first, `isoWeek` rather than `week`, because dayjs's
   * plain week starts on Sunday in the English locale and the Polish calendar
   * this product prints starts on Monday whatever the UI language is.
   */
  const weeks = useMemo(() => {
    const last = month.endOf('month').endOf('isoWeek')
    const result: Dayjs[][] = []
    let cursor = month.startOf('month').startOf('isoWeek')

    while (!cursor.isAfter(last, 'day')) {
      const weekStart = cursor
      result.push(
        Array.from({ length: DAYS_IN_WEEK }, (_, index) =>
          weekStart.add(index, 'day')
        )
      )
      cursor = cursor.add(DAYS_IN_WEEK, 'day')
    }

    return result
  }, [month])

  const days = useMemo(() => weeks.flat(), [weeks])

  /*
    A person on one day, plus what they are down for. `absent` drives the dot's
    shape: a filled dot is someone rostered, a hollow ring is an all-day
    absence, so a month of holiday no longer reads as a month of full staffing.
  */
  interface DayWorker {
    userId: string
    absent: boolean
    types: string[]
  }

  /**
   * The distinct employees on each day, earliest start first.
   *
   * A dot stands for a person, not a shift: someone doing a split day is one
   * dot, otherwise a two-shift day would read as two people short-staffing the
   * next one.
   */
  const workersByDate = useMemo(() => {
    const byDate = new Map<string, DayWorker[]>()

    days.forEach(day => {
      const ordered = [...shiftsForDay(day)].sort(
        (a, b) => a.startMinutes - b.startMinutes
      )
      const workers: DayWorker[] = []

      ordered.forEach(shift => {
        const existing = workers.find(worker => worker.userId === shift.userId)
        const absent = isAllDayType(shift.type)
        const typeLabel = t(shiftTypeDefinition(shift.type).labelKey)

        if (!existing) {
          workers.push({ userId: shift.userId, absent, types: [typeLabel] })
          return
        }

        /*
          One dot per person, so a split day stays one dot. When that person has
          both a shift and an absence the dot reads as present: being rostered is
          the fact that matters for a head count, and the tooltip lists both.
        */
        if (!absent) existing.absent = false
        if (!existing.types.includes(typeLabel)) existing.types.push(typeLabel)
      })

      byDate.set(toDateKey(day), workers)
    })

    return byDate
  }, [days, shiftsForDay, t])

  // Taken from the first rendered week rather than from a hand-written list, so
  // the labels can never fall out of step with the columns under them.
  const weekdays = useMemo(
    () =>
      (weeks[0] ?? []).map(day => ({
        key: toDateKey(day),
        label: day.locale(i18n.language).format('ddd')
      })),
    [weeks, i18n.language]
  )

  return (
    <div className={styles.month}>
      <div className={styles.weekdayRow}>
        {weekdays.map(weekday => (
          <div
            key={weekday.key}
            className={`body-bold-medium ${styles.weekdayHeader}`}
          >
            {weekday.label}
          </div>
        ))}
      </div>

      <div className={styles.gridWrapper}>
        {/*
          The native bar is hidden in CSS and this one is drawn over the
          content, so it is not decoration, it is the only scrollbar the grid
          has on a viewport too short for six week rows.
        */}
        <OverlayScrollbar targetRef={gridRef} />

        <div className={styles.grid} ref={gridRef}>
          {/*
            One grid for both states. The loading branch differs only by the
            dots it leaves out, headers, day numbers and cell geometry are the
            loaded ones, so nothing moves when the shifts land. No grey
            placeholder blocks: invented dots read as real people rostered.
          */}
          {days.map(day => {
            const dateKey = toDateKey(day)
            const workers = isLoading ? [] : (workersByDate.get(dateKey) ?? [])
            const visible = workers.slice(0, MAX_VISIBLE_DOTS)
            const hidden = workers.slice(MAX_VISIBLE_DOTS)
            const isOutside = !day.isSame(month, 'month')
            const dayLabel = day.locale(i18n.language).format('D MMMM YYYY')
            const openLabel = t('SCHEDULE.MONTH.OPEN_DAY', { date: dayLabel })

            return (
              <button
                type="button"
                key={dateKey}
                className={`${styles.dayCell} ${
                  isOutside ? styles.outside : ''
                } ${isToday(day) ? styles.today : ''}`}
                onClick={() => onSelectDay(day)}
                // The dots are decorative to a screen reader, their colours
                // mean nothing without sight, so the head count is spoken as
                // part of the cell's own name instead.
                aria-label={
                  workers.length
                    ? `${openLabel} - ${t('SCHEDULE.MONTH.WORKING', {
                        count: workers.length
                      })}`
                    : openLabel
                }
              >
                <span className={`body-bold-small ${styles.dayNumber}`}>
                  {day.format('D')}
                </span>

                {workers.length > 0 && (
                  <span className={styles.dots}>
                    {visible.map(worker => (
                      <span
                        key={worker.userId}
                        className={`${styles.dot} ${
                          worker.absent ? styles.dotAbsent : ''
                        }`}
                        // The only way a colour is decodable with a pointer.
                        // The entry types come with it, because shape alone
                        // cannot say whether this is holiday or sick leave.
                        title={`${employeeName(worker.userId)} - ${worker.types.join(', ')}`}
                        style={
                          worker.absent
                            ? { borderColor: employeeColor(worker.userId) }
                            : { backgroundColor: employeeColor(worker.userId) }
                        }
                      />
                    ))}

                    {hidden.length > 0 && (
                      <span
                        className={`body-small ${styles.more}`}
                        title={hidden
                          .map(worker => employeeName(worker.userId))
                          .join(', ')}
                      >
                        {t('SCHEDULE.MONTH.MORE', { count: hidden.length })}
                      </span>
                    )}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default MonthCalendar
